/**
 * Orders: list, detail, create, invoices, refunds.
 * Conventions: see `common.ts`.
 */
const orders: Record<string, string> = {
  title: 'Orders',
  back_to_orders: 'Back to orders',
  create_order: 'Create order',
  in_view: '{total} in this view',
  exporting: 'Exporting…',
  open_pdf: 'Open PDF',
  view_order: 'View order',

  'payment_method.RAZORPAY': 'Online payment (Razorpay)',
  'payment_method.COD': 'Cash on delivery',
  'payment_method.WHATSAPP': 'Order via WhatsApp',

  'sort.sort_by': 'Sort by',
  'sort.newest': 'Newest first',
  'sort.oldest': 'Oldest first',
  'sort.total_desc': 'Highest total',
  'sort.total_asc': 'Lowest total',
  'sort.amount_desc': 'Highest amount',
  'sort.amount_asc': 'Lowest amount',

  'segment.aria_label': 'Order views',
  'segment.all': 'All',
  'segment.pending': 'Pending payment',
  'segment.open': 'To fulfil',
  'segment.shipped': 'Shipped',
  'segment.delivered': 'Delivered',
  'segment.returns': 'Returns',
  'segment.cancelled': 'Cancelled',

  'columns.title': 'Columns',
  'columns.choose': 'Choose columns',
  'columns.reset': 'Reset to default',
  'columns.payment_status': 'Payment status',
  'columns.payment_method': 'Payment method',

  'table.order': 'Order',
  'table.date': 'Date',
  'table.customer': 'Customer',
  'table.status': 'Status',
  'table.payment': 'Payment',
  'table.items': 'Items',
  'table.method': 'Method',
  'table.total': 'Total',
  'table.product': 'Product',
  'table.price': 'Price',
  'table.qty': 'Qty',

  'toolbar.search_label': 'Search orders',
  'toolbar.search_placeholder': 'Search by order number, email or phone',
  'toolbar.mark_confirmed': 'Mark confirmed',
  'toolbar.cancel_orders': 'Cancel orders',
  'toolbar.cannot_edit': 'You cannot edit orders.',

  'list.loading': 'Loading your orders…',
  'list.update_failed': 'Could not update those orders.',
  'list.select_all': 'Select all orders on this page',
  'list.select_order': 'Select {number}',
  'list.outcome.cancelled_one': '{count} order cancelled.',
  'list.outcome.cancelled_other': '{count} orders cancelled.',
  'list.outcome.confirmed_one': '{count} order confirmed.',
  'list.outcome.confirmed_other': '{count} orders confirmed.',
  'list.outcome.cancelled_skipped_one':
    '{count} order cancelled · {skipped} skipped (not allowed from their current status).',
  'list.outcome.cancelled_skipped_other':
    '{count} orders cancelled · {skipped} skipped (not allowed from their current status).',
  'list.outcome.confirmed_skipped_one':
    '{count} order confirmed · {skipped} skipped (not allowed from their current status).',
  'list.outcome.confirmed_skipped_other':
    '{count} orders confirmed · {skipped} skipped (not allowed from their current status).',
  'list.empty_search_title': 'No orders match that search',
  'list.empty_search_description': 'Try a different order number, email address or phone number.',
  'list.empty_title': 'Your orders will show here',
  'list.empty_description':
    'This is where you will fulfil orders, collect payments and track progress.',
  'list.empty_segment_title': 'Nothing in this view',
  'list.empty_segment_description': 'No orders are in these statuses right now. Try another tab.',

  'detail.loading': 'Loading order…',
  'detail.load_failed': 'This order could not be loaded.',
  'detail.placed': 'Placed {date} · {method}',
  'detail.refund': 'Refund',
  'detail.refunded_partial':
    '{refunded} has been refunded on this order, leaving {refundable} refundable.',
  'detail.refunded_full': '{refunded} has been refunded on this order in full.',
  'detail.invoice_ready': 'Invoice ready.',
  'detail.opening': 'Opening…',
  'detail.open_the_pdf': 'Open the PDF',
  'detail.move_along': 'Move this order along',
  'detail.final_state': 'This order has reached a final state.',
  'detail.generating': 'Generating…',
  'detail.gst_invoice': 'GST invoice',
  'detail.ship_rest': 'Ship the rest',
  'detail.create_shipment': 'Create shipment',
  'detail.shipping': 'Shipping',
  'detail.all_shipments': 'All shipments →',
  'detail.loading_shipments': 'Loading shipments…',
  'detail.no_shipments': 'Nothing has been booked for this order yet.',
  'detail.shipment_return': 'Return',
  'detail.recorded_by_hand': 'Recorded by hand',
  'detail.shipment_booked': 'Booked {date}',
  'detail.shipment_booked_expected': 'Booked {date} · expected {expected}',
  'detail.items': 'Items',
  'detail.subtotal': 'Subtotal',
  'detail.discount_code': 'Discount {code}',
  'detail.bundle_discount': 'Bundle discount',
  'detail.delivery': 'Delivery',
  'detail.delivery_named': 'Delivery · {name}',
  'detail.free': 'Free',
  'detail.cod_fee': 'Cash on delivery fee',
  'detail.gst_included': 'GST included',
  'detail.ship_to': 'Ship to',
  'detail.call': 'Call {name}',
  'detail.whatsapp': 'Message {name} on WhatsApp',
  'detail.quoted_for': 'Quoted for {date}',
  'detail.quoted_days_one': 'Quoted at {count} day',
  'detail.quoted_days_other': 'Quoted at {count} days',
  'detail.history': 'History',
  'detail.shipment_recorded': 'Shipment recorded.',
  'detail.booked_with': 'Booked with {carrier}.',
  'detail.booked_with_courier': 'Booked with the courier.',
  'detail.errors.update': 'Could not update the order.',
  'detail.errors.refund': 'Could not send the refund.',
  'detail.errors.invoice': 'Could not generate the invoice.',
  'detail.errors.open_invoice': 'Could not open the invoice.',

  'detail.mark.PENDING': 'Mark pending',
  'detail.mark.CONFIRMED': 'Mark confirmed',
  'detail.mark.PROCESSING': 'Mark processing',
  'detail.mark.PACKED': 'Mark packed',
  'detail.mark.SHIPPED': 'Mark shipped',
  'detail.mark.DELIVERED': 'Mark delivered',
  'detail.mark.CANCELLED': 'Mark cancelled',
  'detail.mark.RETURN_REQUESTED': 'Mark return requested',
  'detail.mark.RETURNED': 'Mark returned',
  'detail.mark.REFUNDED': 'Mark refunded',

  'history_status.PENDING': 'PENDING',
  'history_status.CONFIRMED': 'CONFIRMED',
  'history_status.PROCESSING': 'PROCESSING',
  'history_status.PACKED': 'PACKED',
  'history_status.SHIPPED': 'SHIPPED',
  'history_status.DELIVERED': 'DELIVERED',
  'history_status.CANCELLED': 'CANCELLED',
  'history_status.RETURN_REQUESTED': 'RETURN REQUESTED',
  'history_status.RETURNED': 'RETURNED',
  'history_status.REFUNDED': 'REFUNDED',

  'refund.title': 'Refund {order}',
  'refund.sending': 'Sending…',
  'refund.submit': 'Refund {amount}',
  'refund.description':
    'The money goes back to the shopper through Razorpay, usually landing in five to seven working days. This cannot be undone.',
  'refund.still_refundable': 'Still refundable',
  'refund.partial': 'Refund part of it instead',
  'refund.amount': 'Amount',
  'refund.amount_invalid': 'Enter an amount between 0.01 and {max}.',
  'refund.reason': 'Reason',
  'refund.reason_placeholder': 'e.g. Damaged in transit',
  'refund.reason_hint': "Kept on the order's history and sent to Razorpay as a note.",

  'create.search_products': 'Search products',
  'create.search_products_placeholder': 'Search products to add',
  'create.searching': 'Searching…',
  'create.no_products': 'No products match that search.',
  'create.remove_customer': 'Remove customer',
  'create.search_customers': 'Search customers',
  'create.search_customers_placeholder': 'Search by name, email or phone',
  'create.no_customers': 'No customer matches. Fill in the details below to add one with the order.',
  'create.errors.create': 'Could not create the order.',
  'create.no_permission': 'You do not have permission to create orders.',
  'create.mark_as_paid': 'Mark as paid',
  'create.creating': 'Creating…',
  'create.products': 'Products',
  'create.add_custom_item': 'Add custom item',
  'create.item_name': 'Item name',
  'create.item_name_placeholder': 'Gift wrapping',
  'create.quantity_of': 'Quantity of {name}',
  'create.remove_line': 'Remove {name}',
  'create.custom_item': 'Custom item',
  'create.no_lines': 'Search the catalog or add a custom item to start the order.',
  'create.payment': 'Payment',
  'create.discount': 'Discount',
  'create.discount_percent': '({value}%)',
  'create.discount_fixed': '(fixed)',
  'create.remove_discount': 'Remove discount',
  'create.shipping': 'Shipping',
  'create.shipping_amount': 'Shipping amount',
  'create.paid_note': 'The order will be recorded as paid.',
  'create.due_note': 'Payment will be due on the order (cash on delivery / collect later).',
  'create.no_payment': 'Add a product to calculate the total and view payment options.',
  'create.customer': 'Customer',
  'create.new_customer': 'Or add a new customer',
  'create.name': 'Name',
  'create.email': 'Email',
  'create.phone_optional': 'Phone (optional)',
  'create.notes': 'Notes',
  'create.order_note': 'Order note',
  'create.order_note_placeholder': 'Only you and your staff can see this.',
  'create.add_discount': 'Add discount',
  'create.percent': 'Percent',
  'create.amount': 'Amount',

  'invoices.title': 'Invoices',
  'invoices.loading': 'Loading your invoices…',
  'invoices.invoiced': 'Invoiced',
  'invoices.taxable_value': 'Taxable value',
  'invoices.gst_collected': 'GST collected',
  'invoices.gst_split': 'CGST+SGST {intra} · IGST {inter}',
  'invoices.one_per_order': 'One per confirmed order',
  'invoices.excludes_void': 'Excludes {count} voided (order cancelled)',
  'invoices.open_failed': 'Could not open that invoice.',
  'invoices.empty_search_title': 'No invoices match that search',
  'invoices.empty_search_description':
    'Try a different invoice number, order number, email address or phone number.',
  'invoices.empty_title': 'No invoices in this view',
  'invoices.empty_description':
    'An invoice is generated from an order once it is confirmed. Widen the period or the GST filter to see earlier ones.',
  'invoices.show_all': 'Show all invoices',
  'invoices.table.invoice': 'Invoice',
  'invoices.table.place_of_supply': 'Place of supply',
  'invoices.table.taxable': 'Taxable',
  'invoices.table.gst': 'GST',
  'invoices.actions_for': 'Actions for {number}',
  'invoices.view_details': 'View details',
  'invoices.download_pdf': 'Download PDF',
  'invoices.copy_number': 'Copy number',
  'invoices.search_label': 'Search invoices',
  'invoices.search_placeholder': 'Search by invoice number, order number or buyer',
  'invoices.period': 'Period',
  'invoices.gst_type': 'GST type',
  'invoices.tax.all': 'All invoices',
  'invoices.tax.intra': 'CGST + SGST (within state)',
  'invoices.tax.inter': 'IGST (inter-state)',
  'invoices.period.this_fy': 'This financial year',
  'invoices.period.last_fy': 'Last financial year',
  'invoices.period.this_quarter': 'This quarter',
  'invoices.period.this_month': 'This month',
  'invoices.period.last_month': 'Last month',
  'invoices.period.all': 'All time',

  'invoice.title': 'Invoice',
  'invoice.title_number': 'Invoice {number}',
  'invoice.date_place': '{date} · Place of supply: {place}',
  'invoice.loading': 'Loading invoice…',
  'invoice.load_failed': 'This invoice could not be loaded.',
  'invoice.void': 'Void',
  'invoice.void_note':
    'This order was cancelled after it was invoiced, so this invoice is void. Its PDF is stamped CANCELLED and is not valid for input tax credit.',
  'invoice.sold_by': 'Sold by',
  'invoice.billed_to': 'Billed to',
  'invoice.gstin': 'GSTIN {gstin}',
  'invoice.hsn': 'HSN {code}',
  'invoice.item': 'Item',
  'invoice.amount': 'Amount',

  // --- Sales channel (WhatsApp Commerce) ---
  'columns.sales_channel': 'Channel',
  'table.channel': 'Channel',
  'filters.channel': 'Channel',
  'filters.channel_all': 'All channels',
  'sales_channel.STOREFRONT': 'Online store',
  'sales_channel.WHATSAPP': 'WhatsApp',
  'sales_channel.MANUAL': 'Manual',
  'sales_channel.INSTAGRAM': 'Instagram',
  'sales_channel.FACEBOOK': 'Facebook',
  'sales_channel.MARKETPLACE': 'Marketplace',
  'sales_channel.API': 'API',
  'detail.view_whatsapp_conversation': 'View WhatsApp conversation',
};

export default orders;
